/**
 * 知识点关联（refType / refId 补齐）
 * ------------------------------------------------------------
 * 为什么需要它：
 *   records.js 的错题聚合 `if (!a.refId) continue` 会直接丢弃没有 refId 的作答。
 *   如果题目的 refId 为空，孩子答错这道题**不会进入错题本**，这是硬性缺陷。
 *
 * 规则（按优先级，绝不瞎猜）：
 *   1) 题目已带 refType + refId，且能在本单元内容里找到 → 直接沿用
 *   2) 有 grammarCategory → 按 category 精确匹配本单元 grammar，匹配到就挂到该语法点
 *   3) 没有分类但题型是语法题（skill==='G'）→ 挂到「待确认」，不进任何语法点，但仍保留 refId 以便入错题本
 *   4) 其余 → 挂到题目自身 id，保证错题一定入库
 *
 * 关键：refType='grammar' 时 refId 必须指向**真实存在的 grammar.id**，
 * 否则错题本里的「知识点」名字会变成一串乱码 id。匹配不到就回退，不要伪造 grammar。
 */

/** 关键词 → 语法类别的弱匹配表（用于把「there be 题」这类自然描述收敛到单元里的语法点） */
const CATEGORY_ALIASES = [
  { keys: ['there be', 'therebe', '存在句'], hint: ['there be'] },
  { keys: ['can', '情态动词'], hint: ['can'] },
  { keys: ['let', 'let us', "let's", '祈使'], hint: ["let"] },
  { keys: ['复数', '单数', 'plural', '名词'], hint: ['复数', '数字', 'plural'] },
  { keys: ['be 动词', 'be动词', 'am is are'], hint: ['be 动词', 'be动词'] },
  { keys: ['一般现在时', 'present'], hint: ['一般现在时', 'present'] },
  { keys: ['现在进行时', 'ing'], hint: ['现在进行时', 'ing'] },
  { keys: ['介词', 'preposition', 'in on under'], hint: ['介词'] },
  { keys: ['疑问句', 'question'], hint: ['疑问句'] },
];

function norm(s) {
  return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * 在 grammar 列表里找与某个分类名最匹配的语法点
 * @param {Array} grammar 本单元语法点数组（每个含 id / category）
 * @param {string} category 题目上的分类名（可能为空、可能是自然语言描述）
 * @returns {object|null}
 */
export function matchGrammar(grammar, category) {
  const list = Array.isArray(grammar) ? grammar.filter(g => g && g.id) : [];
  if (!list.length) return null;
  const key = norm(category);

  if (key) {
    // ① 完全相等
    const exact = list.find(g => norm(g.category) === key);
    if (exact) return exact;
    // ② 互相包含（题目分类「There be 句型练习」↔ 语法点「There be 句型」）
    const contains = list.find(g => {
      const gc = norm(g.category);
      return gc && (key.includes(gc) || gc.includes(key));
    });
    if (contains) return contains;
    // ③ 别名表弱匹配
    for (const alias of CATEGORY_ALIASES) {
      if (!alias.keys.some(k => key.includes(k))) continue;
      const hit = list.find(g => alias.hint.some(h => norm(g.category).includes(norm(h))));
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * 给一单元的题目补齐 refType / refId / grammarCategory
 * @param {Array} questions 题目数组（原地不修改，返回新数组）
 * @param {Array} grammar 本单元语法点
 * @param {object} [opts] { fallbackPrefix: 'q' }
 * @returns {Array} 补齐后的题目
 */
export function attachQuestionRefs(questions, grammar, opts = {}) {
  const list = Array.isArray(questions) ? questions : [];
  const grammarIds = new Set((grammar || []).map(g => g.id));
  const categories = new Set((grammar || []).map(g => norm(g.category)));

  return list.map(q => {
    const qid = q.id || `${opts.fallbackPrefix || 'q'}`;
    // ① 已带有效引用 → 保留，但把分类名对齐成语法点上的正式名字
    if (q.refId && (!q.refType || q.refType === 'grammar')) {
      const okType = q.refType === 'grammar' ? grammarIds.has(q.refId) : !!q.refType;
      if (okType) {
        const g = grammarIds.has(q.refId) ? grammar.find(x => x.id === q.refId) : null;
        return {
          ...q,
          refType: q.refType || 'grammar',
          refId: q.refId,
          grammarCategory: q.grammarCategory || g?.category || '',
        };
      }
    }

    const category = q.grammarCategory || '';
    const g = matchGrammar(grammar, category);

    if (g) {
      return { ...q, refType: 'grammar', refId: g.id, grammarCategory: g.category };
    }

    // 分类存在但单元里没有对应语法点 → 不猜，标「待确认」，但 refId 仍落到题目自身，
    // 这样错题本照样能收集到这道题，只是归类为「待确认知识点」。
    const isGrammarQ = q.skill === 'G' || !!category;
    return {
      ...q,
      refType: 'grammar',
      // 用题目自身 id 做 refId，保证错题能入库；category 为空表示「待确认」
      refId: `q-${qid}`,
      grammarCategory: category && !categories.has(norm(category)) ? category : category,
      refSynthetic: true,          // 标记：这是回退出来的引用，不是真实语法点
    };
  });
}

/** 判断一条题目引用是不是回退出来的（供错题本展示「待确认知识点」） */
export function isSyntheticRef(q) {
  return !!q?.refSynthetic;
}
